/**
 * One metre scale for everything, at every zoom (16.101, Don's direction).
 *
 * ## What was actually wrong
 *
 * Don read 16.100's separation table -- ship radius 237m at Close, 158m at
 * Medium, 89m at Far -- and reasonably asked whether the metre scale itself
 * shifted with zoom, and whether that meant zoom affected weapon range.
 *
 * **It did not, and it never has.** `zoom` was read in exactly two places in the
 * whole Expedition View: `scalePxPerMeter()`, the world-to-screen projection, and
 * `os()`, the sprite draw size. Every range in the game -- weapons, detection,
 * stop distances, docking, mining -- compares world coordinates in metres with
 * `Math.hypot`. Zoom could not reach any of them. Game logic was already a
 * visual-only zoom.
 *
 * The real defect was in the other place, and Don's instinct found it from the
 * outside: **sprites were sized in pixels, not metres.** `os(13)` drew a ship
 * 13px across at Medium and 52px at Close regardless of the world, so the
 * object's apparent size *in metres* changed with zoom -- 158m wide at Medium,
 * 237m at Close. That is what 16.100's table measured, and it is why choosing a
 * collision radius from it felt arbitrary: there was no world radius to choose,
 * because nothing in the game had ever defined one.
 *
 * ## The fix
 *
 * Every object gets a real radius in metres, and drawing is
 * `radiusM * pxPerMeter`. Zoom then changes pixels and nothing else, which is
 * exactly what Don asked for.
 *
 * The radii below are **calibrated to the current Medium-zoom appearance**, on
 * purpose: Medium is the default, so this preserves the look the game already
 * has where most play happens and only corrects Close and Far, which were the
 * inconsistent ones. Derived as `basePx / 0.0823 px-per-metre` from the real draw
 * calls, then rounded:
 *
 * | Object | old base px | Medium appearance | locked radius |
 * |---|---|---|---|
 * | Ship | 13 | 158m | **160m** |
 * | Asteroid / wreck | 9 | 109m | **110m** |
 * | Jump Gate | 26 | 316m | **320m** |
 * | Station | 60 | 729m | **730m** |
 * | Planet | 70 | 851m | **850m** |
 *
 * These are chosen, not locked by any earlier section -- flagged as such rather
 * than presented as derived from the design doc. The scene is a stylised few
 * thousand metres across, so a "planet" at 850m is a local landmark rather than
 * an astronomical body; that was already true of the pixel sizes.
 *
 * This SUPERSEDES 16.91's `objectScalePx` for world objects. That function
 * existed to keep objects visible when zoomed out (4x Close, 1/4 Far), and true
 * world scaling turns out to serve that goal better than the multipliers did: a
 * ship is 5.9px at Far under real scaling versus 3.25px under the old 1/4
 * multiplier -- bigger, not smaller. `MIN_DRAW_PX` keeps the promise explicit for
 * anything that would otherwise vanish.
 */

/** Legibility floor. Not a scale change -- a guarantee that nothing disappears. */
export const MIN_DRAW_PX = 3;

export const SHIP_RADIUS_M = 160;
export const ASTEROID_RADIUS_M = 110;
export const WRECK_RADIUS_M = 110;
export const GATE_RADIUS_M = 320;
export const STATION_RADIUS_M = 730;
export const PLANET_RADIUS_M = 850;

/**
 * Radius in metres for a fixed object kind, so the draw path and the collision
 * path read the same number from the same place.
 */
export function fixedObjectRadiusM(kind: "asteroid" | "wreck" | "gate" | "planet" | "station"): number {
  switch (kind) {
    case "asteroid":
      return ASTEROID_RADIUS_M;
    case "wreck":
      return WRECK_RADIUS_M;
    case "gate":
      return GATE_RADIUS_M;
    case "station":
      return STATION_RADIUS_M;
    case "planet":
      return PLANET_RADIUS_M;
  }
}

/** Pixels to draw a world radius at, floored so nothing becomes invisible. */
export function drawRadiusPx(radiusM: number, pxPerMeter: number): number {
  return Math.max(MIN_DRAW_PX, radiusM * pxPerMeter);
}

/**
 * How close a ship may come to something before it is overlapping it.
 *
 * Only ships and asteroids are solid (Don's rule: ships may pass over stations,
 * planets and wrecks). Kept next to the radii so the two cannot drift apart.
 */
export const SHIP_TO_SHIP_CLEARANCE_M = SHIP_RADIUS_M * 2;
export const SHIP_TO_ASTEROID_CLEARANCE_M = SHIP_RADIUS_M + ASTEROID_RADIUS_M;

/**
 * Mining Laser range (16.101, Don's direction) -- 100m before this section.
 *
 * It lives here, beside the clearance, because the two constrain each other and
 * that constraint is easy to break from either side: **a ship that cannot enter
 * an asteroid must still be able to shoot one.** At the old 100m the module was
 * unusable by any normal approach -- Move To stopped at 150m and Orbit Near at
 * 450m, both already outside it -- and once asteroids became solid the ship
 * physically could not get closer than 270m. Don's fix is to match the Laser
 * Turret's 1400m, which clears the surface by a wide margin.
 *
 * The invariant is asserted in shipSeparation.test.ts rather than left implied.
 */
export const MINING_LASER_RANGE_M = 1400;
